import React from 'react';
import { TEMA } from '../theme';
import { useAuth } from '../context/AuthContext';

interface Offertstopp {
  namn: string;
  sub: string;
  dagar: number;
}

interface Matchning {
  vin: string;
  köpare: string;
  poäng: number;
  nivå: 'featured' | 'positive' | 'neutral';
}

interface PipelineSteg {
  namn: string;
  antal: number;
  färg: 'accent' | 'positive' | 'faint';
}

interface Order {
  namn: string;
  status: string;
  datum: string;
  kr: number;
}

// Mockdata tills respektive modul (Anbudskalender, Affärsmöjligheter osv.)
// har riktiga endpoints att hämta ifrån.
const OFFERTSTOPP: Offertstopp[] = [
  { namn: '691-23 · Muscadet de Sèvre et Maine, eko', sub: 'Systembolaget · Ordinarie sortiment', dagar: 11 },
  { namn: '693-05 · Riesling Kabinett', sub: 'Systembolaget · Tillfälligt sortiment', dagar: 18 },
  { namn: '690-88 · Grenache Rosé', sub: 'Systembolaget · Ordinarie sortiment', dagar: 25 },
];

const MATCHNINGAR: Matchning[] = [
  { vin: '691-23 Muscadet de Sèvre et Maine', köpare: 'Restaurang Enoteca', poäng: 81, nivå: 'featured' },
  { vin: '693-05 Riesling Kabinett', köpare: 'Brattlöf Group AB', poäng: 68, nivå: 'positive' },
  { vin: '691-10 Cigales', köpare: 'Babette AB', poäng: 54, nivå: 'neutral' },
];

const PIPELINE_MAX = 15;
const PIPELINE: PipelineSteg[] = [
  { namn: 'Utkast', antal: 4, färg: 'faint' },
  { namn: 'Skickad', antal: 6, färg: 'faint' },
  { namn: 'Förhandling', antal: 2, färg: 'accent' },
  { namn: 'Vunnen', antal: 5, färg: 'positive' },
  { namn: 'Förlorad', antal: 3, färg: 'faint' },
];

const LIVSCYKEL = [
  { etikett: 'Ny lansering', antal: 3, färg: TEMA.positive },
  { etikett: 'Etablerad', antal: 12, färg: TEMA.ink },
  { etikett: 'Utgående', antal: 2, färg: TEMA.accent },
];

const ORDRAR: Order[] = [
  { namn: '691-23 Muscadet, eko', status: 'Levererat', datum: '2026-09-10', kr: 4800 },
  { namn: '690-88 Grenache Rosé', status: 'Fakturerat', datum: '2026-09-05', kr: 2400 },
];

const PIPELINE_FÄRG: Record<PipelineSteg['färg'], string> = {
  accent: TEMA.accent,
  positive: TEMA.positive,
  faint: TEMA.inkFaint,
};

const MATCH_FÄRG: Record<Matchning['nivå'], string> = {
  featured: TEMA.accent,
  positive: TEMA.positive,
  neutral: TEMA.inkFaint,
};

function formatKr(v: number): string {
  return v.toLocaleString('sv-SE') + ' kr';
}

function hälsning(): string {
  const h = new Date().getHours();
  if (h < 10) return 'God morgon';
  if (h < 12) return 'God förmiddag';
  if (h < 18) return 'God eftermiddag';
  return 'God kväll';
}

function dagensDatum(): string {
  return new Date()
    .toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })
    .toUpperCase();
}

const panelStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
  marginBottom: '24px',
};

const panelHeadStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '18px',
};

const panelTitleStyle: React.CSSProperties = {
  fontFamily: "'Fraunces', serif",
  fontSize: '16px',
  color: TEMA.ink,
  margin: 0,
};

const panelLinkStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '12px',
  color: TEMA.accent,
  textDecoration: 'none',
};

const statCardStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
};

const statLabelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: TEMA.inkFaint,
  marginBottom: '12px',
};

const statValueStyle: React.CSSProperties = {
  fontFamily: "'Fraunces', serif",
  fontSize: '34px',
  color: TEMA.ink,
  marginBottom: '10px',
};

const ctaStyle: React.CSSProperties = {
  fontSize: '13px',
  color: TEMA.accent,
  textDecoration: 'none',
};

const rowBorderStyle: React.CSSProperties = {
  borderBottom: `1px solid ${TEMA.border}`,
};

export default function AnbudDashboard() {
  const { profil } = useAuth();

  return (
    <div>
      <p style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase', color: TEMA.accent, marginBottom: '10px' }}>
        ANBUD · {dagensDatum()}
      </p>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '30px', marginBottom: '36px', color: TEMA.ink }}>
        {hälsning()}{profil?.namn ? `, ${profil.namn}` : ''}
      </h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '40px' }}>
        <div style={statCardStyle}>
          <div style={statLabelStyle}>Aktiva anbud</div>
          <div style={statValueStyle}>14</div>
          <a style={ctaStyle} href="/anbud/affarsmojligheter">Offertförfrågningar →</a>
        </div>
        <div style={statCardStyle}>
          <div style={statLabelStyle}>Offertstopp inom 7 dagar</div>
          <div style={statValueStyle}>3</div>
          <a style={ctaStyle} href="/anbudskalender">Kalender →</a>
        </div>
        <div style={statCardStyle}>
          <div style={statLabelStyle}>Vunna denna månad</div>
          <div style={statValueStyle}>5</div>
          <a style={ctaStyle} href="/anbud/orderhistorik">Orderhistorik →</a>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '24px' }}>
        {/* Vänster kolumn */}
        <div>
          <div style={panelStyle}>
            <div style={panelHeadStyle}>
              <h3 style={panelTitleStyle}>Kommande offertstopp</h3>
              <a style={panelLinkStyle} href="/anbudskalender">Kalender →</a>
            </div>
            {OFFERTSTOPP.map((rad, i) => (
              <div
                key={rad.namn}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', ...(i < OFFERTSTOPP.length - 1 ? rowBorderStyle : {}),
                }}
              >
                <div>
                  <div style={{ fontSize: '14px', color: TEMA.ink }}>{rad.namn}</div>
                  <div style={{ fontSize: '12px', color: TEMA.inkFaint, marginTop: '2px' }}>{rad.sub}</div>
                </div>
                <span
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', letterSpacing: '0.04em',
                    padding: '3px 10px', borderRadius: '2px', whiteSpace: 'nowrap',
                    border: `1px solid ${rad.dagar <= 14 ? TEMA.accent : TEMA.border}`,
                    color: rad.dagar <= 14 ? TEMA.accent : TEMA.inkSoft,
                  }}
                >
                  {rad.dagar} dagar
                </span>
              </div>
            ))}
          </div>

          <div style={panelStyle}>
            <div style={panelHeadStyle}>
              <h3 style={panelTitleStyle}>Senaste matchningsförslag</h3>
              <a style={panelLinkStyle} href="/anbud/aktiviteter">Aktiviteter →</a>
            </div>
            {MATCHNINGAR.map((rad, i) => (
              <div
                key={rad.vin}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', ...(i < MATCHNINGAR.length - 1 ? rowBorderStyle : {}),
                }}
              >
                <div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', color: TEMA.ink }}>{rad.vin}</div>
                  <div style={{ fontSize: '12px', color: TEMA.inkFaint, marginTop: '2px' }}>Kanal-inköpare: {rad.köpare}</div>
                </div>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', color: MATCH_FÄRG[rad.nivå] }}>
                  {rad.poäng}
                  {rad.nivå === 'neutral' ? (
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: TEMA.inkFaint, display: 'inline-block' }} />
                  ) : (
                    <span style={{ width: '10px', height: '10px', border: `2px solid ${MATCH_FÄRG[rad.nivå]}`, borderRadius: '50%', display: 'inline-block' }} />
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Höger kolumn */}
        <div>
          <div style={panelStyle}>
            <div style={panelHeadStyle}>
              <h3 style={panelTitleStyle}>Offerter</h3>
              <a style={panelLinkStyle} href="/anbud/offertpipeline">Visa alla →</a>
            </div>
            {PIPELINE.map(steg => (
              <div key={steg.namn} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 0' }}>
                <span style={{ width: '100px', fontSize: '13px', color: TEMA.inkSoft, flexShrink: 0 }}>{steg.namn}</span>
                <div style={{ flex: 1, height: '6px', background: TEMA.border, borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: '3px', background: PIPELINE_FÄRG[steg.färg], width: `${(steg.antal / PIPELINE_MAX) * 100}%` }} />
                </div>
                <span style={{ width: '24px', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', color: TEMA.ink, flexShrink: 0 }}>{steg.antal}</span>
              </div>
            ))}
          </div>

          <div style={panelStyle}>
            <div style={panelHeadStyle}>
              <h3 style={panelTitleStyle}>Produktlivscykel</h3>
              <a style={panelLinkStyle} href="/anbud/produktlivscykel">Visa alla →</a>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
              {LIVSCYKEL.map(cell => (
                <div key={cell.etikett} style={{ textAlign: 'center', padding: '14px 8px', border: `1px solid ${TEMA.border}`, borderRadius: '6px' }}>
                  <div style={{ fontFamily: "'Fraunces', serif", fontSize: '22px', color: cell.färg }}>{cell.antal}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: TEMA.inkFaint, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {cell.etikett}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={panelStyle}>
            <div style={panelHeadStyle}>
              <h3 style={panelTitleStyle}>Orderhistorik</h3>
              <a style={panelLinkStyle} href="/anbud/orderhistorik">Visa alla →</a>
            </div>
            {ORDRAR.map((rad, i) => (
              <div
                key={rad.namn}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', fontSize: '13px', ...(i < ORDRAR.length - 1 ? rowBorderStyle : {}),
                }}
              >
                <div>
                  <div style={{ color: TEMA.ink }}>{rad.namn}</div>
                  <div style={{ color: TEMA.inkFaint, fontSize: '12px', marginTop: '2px' }}>{rad.status} {rad.datum}</div>
                </div>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: TEMA.inkSoft }}>{formatKr(rad.kr)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}